import { useAuth } from '@/app/context';
import type { GetInstallmentsParams, Installment } from '@/services';
import { useDataTablePageSync } from '@/shared/hooks';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import { formatDateToBrazil } from '@/shared/utils/format-date.util';
import type { UserType } from '@/shared/utils/get-group';
import { f, useFilters } from '@/shared/utils';
import { SearchFilterField } from '@/shared/ui';
import { toaster } from '@/shared/ui/toaster.component';
import {
  Button,
  DataTable,
  DataTableColumn,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import {
  Calendar,
  Download,
  FileDown,
  Funnel,
  FunnelX,
  Loader2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  CardRow,
  ReportCard,
  ReportCardBody,
  ReportCardHeader,
  ReportCardSkeleton,
} from '../shared/report-mobile-card.component';
import {
  useAdvisoryOptions,
  useBrokerOptions,
  useReportsFilterOptions,
} from '../../hooks/use-reports-filter-options.hook';
import { useInstallments } from '../../hooks/use-installments.hook';
import { useInstallmentsSummary } from '../../hooks/use-installments-summary.hook';
import { useInstallmentsExport } from '../../hooks/use-installments-export.hook';
import { useInstallmentBoleto } from '../../hooks/use-installment-boleto.hook';
import {
  isRowBoletoBlocked,
  rowBoletoBlockedReason,
} from '../../utils/installment-boleto.util';
import { ExtratoParcelasFilters } from './extrato-parcelas-filters.component';
import { InstallmentsSummaryCards } from './installments-summary-cards.component';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

/** Colunas ordenáveis: id da coluna → chave de ordenação do backend. */
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  advisory: 'assessoria',
  broker: 'corretor',
  product: 'produto',
  client: 'cliente',
  policy: 'apolice',
  issue: 'emissao',
};
const COLUMN_BY_SORT_FIELD: Record<string, string> = Object.fromEntries(
  Object.entries(SORT_FIELD_BY_COLUMN).map(([col, field]) => [field, col])
);

function deriveUserType(groups: string[] | null): UserType | null {
  const list = groups ?? [];
  if (list.some(g => g.startsWith('insurer.'))) return 'insurer';
  if (list.some(g => g.startsWith('advisory.'))) return 'advisory';
  if (list.some(g => g.startsWith('broker.'))) return 'broker';
  return null;
}

function addMonths(iso: string, months: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const base = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0)
  ).getUTCDate();
  const clampedDay = Math.min(day, lastDay);
  return new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), clampedDay)
  )
    .toISOString()
    .slice(0, 10);
}

function validatePeriod(from: string, to: string): string | undefined {
  if (!from || !to) return undefined;
  if (to < from)
    return 'A data final do período deve ser maior ou igual à inicial.';
  if (to > addMonths(from, 3)) return 'Selecione um período de até 3 meses';
  return undefined;
}

const money = (value: number | null) => formatCurrency(Number(value ?? 0));

/** Mapeia o rótulo derivado da parcela para o esquema de cor da Tag. */
function statusScheme(
  name: string | null
): 'success' | 'error' | 'warning' | 'gray' {
  const n = (name ?? '').toLowerCase();
  if (n.includes('paga')) return 'success';
  if (n.includes('inadimpl')) return 'error';
  if (n.includes('vencer')) return 'warning';
  return 'gray';
}

export function ExtratoParcelasTab() {
  const { groups } = useAuth();
  const userType = deriveUserType(groups);

  const [defaultPeriod] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { from: iso(from), to: iso(to) };
  });

  const { filters, setFilters, registerFilter } = useFilters({
    page: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(''),
    sortType: f.string().withDefault('asc'),
    search: f.string().withDefault('').withDebounce(500),
    periodFrom: f.string().withDefault(defaultPeriod.from),
    periodTo: f.string().withDefault(defaultPeriod.to),
    periodType: f.string().withDefault('1'),
    advisoryPartyCode: f.string().withDefault(''),
    brokerPartyCode: f.string().withDefault(''),
    productCode: f.string().withDefault(''),
    branchCode: f.string().withDefault(''),
    installmentStatus: f.string().withDefault(''),
  });

  const [showFilters, setShowFilters] = useState(false);

  // "Limpar Seleção" só aparece quando algum filtro do painel diverge do
  // padrão (a busca livre fica fora do painel e não conta). periodType tem
  // default '1', então só conta como ativo quando diferente de '1'.
  const hasActiveFilter =
    Boolean(
      filters.advisoryPartyCode ||
      filters.brokerPartyCode ||
      filters.productCode ||
      filters.branchCode ||
      filters.installmentStatus
    ) ||
    filters.periodType !== '1' ||
    filters.periodFrom !== defaultPeriod.from ||
    filters.periodTo !== defaultPeriod.to;

  const periodError = validatePeriod(filters.periodFrom, filters.periodTo);
  const periodReady = Boolean(
    filters.periodFrom && filters.periodTo && !periodError
  );
  const dataTableRef = useDataTablePageSync<Installment>(filters.page);

  // Filtros que afetam o conjunto (grid e cards).
  const activeFilters = {
    periodFrom: filters.periodFrom,
    periodTo: filters.periodTo,
    periodType: filters.periodType ? Number(filters.periodType) : undefined,
    advisoryPartyCode: filters.advisoryPartyCode || undefined,
    brokerPartyCode: filters.brokerPartyCode || undefined,
    productCode: filters.productCode || undefined,
    branchCode: filters.branchCode ? Number(filters.branchCode) : undefined,
    installmentStatus: filters.installmentStatus || undefined,
    search: filters.search || undefined,
  };

  const queryParams: GetInstallmentsParams = {
    ...activeFilters,
    page: filters.page,
    pageSize: filters.pageSize,
    sortField: filters.sortField || undefined,
    sortType: filters.sortType === 'desc' ? 'desc' : 'asc',
  };

  const {
    data: results,
    isLoading,
    error,
  } = useInstallments(periodReady ? queryParams : {});

  // Cards: dependem SÓ dos filtros — paginar/ordenar não refaz a agregação.
  // Cards ignoram o status (são o próprio breakdown) — não enviam installmentStatus,
  // então clicar num card não recarrega os cards.
  const summaryFilters = {
    periodFrom: activeFilters.periodFrom,
    periodTo: activeFilters.periodTo,
    periodType: activeFilters.periodType,
    advisoryPartyCode: activeFilters.advisoryPartyCode,
    brokerPartyCode: activeFilters.brokerPartyCode,
    productCode: activeFilters.productCode,
    branchCode: activeFilters.branchCode,
    search: activeFilters.search,
  };
  const { data: summary, isLoading: summaryLoading } = useInstallmentsSummary(
    periodReady ? summaryFilters : {}
  );

  const { data: advisoryOptions = [] } = useAdvisoryOptions(
    userType === 'insurer'
  );
  const { data: brokerOptions = [] } = useBrokerOptions(
    userType === 'insurer' || userType === 'advisory'
  );
  const { data: filterOptions } = useReportsFilterOptions();
  const exportMutation = useInstallmentsExport();

  const rows = results?.data ?? [];
  const pagination = results?.meta;

  const showAdvisoryColumn = userType === 'insurer';
  const showBrokerColumn = userType === 'insurer' || userType === 'advisory';

  const columns = useMemo<DataTableColumn<Installment>[]>(() => {
    const cols: DataTableColumn<Installment>[] = [];

    if (showAdvisoryColumn) {
      cols.push({
        id: 'advisory',
        accessorKey: 'advisoryName',
        header: 'Assessoria',
        size: 140,
        cell: ({ row }) => (
          <TwoLine
            top={row.original.advisoryName}
            bottom={row.original.advisoryTaxId}
          />
        ),
      });
    }
    if (showBrokerColumn) {
      cols.push({
        id: 'broker',
        accessorKey: 'brokerName',
        header: 'Corretor',
        size: 140,
        cell: ({ row }) => (
          <TwoLine
            top={row.original.brokerName}
            bottom={row.original.brokerTaxId}
          />
        ),
      });
    }

    cols.push(
      {
        id: 'product',
        accessorKey: 'productName',
        header: 'Produto',
        size: 90,
        cell: ({ row }) => (
          <span className='text-xs text-alba-black-500'>
            {row.original.productName || '-'}
          </span>
        ),
      },
      {
        id: 'client',
        accessorKey: 'clientName',
        header: 'Cliente',
        size: 140,
        cell: ({ row }) => (
          <TwoLine
            top={row.original.clientName}
            bottom={row.original.clientTaxId}
          />
        ),
      },
      {
        id: 'sub',
        accessorKey: 'subNumber',
        header: 'Sub',
        size: 45,
        sortable: false,
        cell: ({ row }) => (
          <span className='text-xs text-alba-black-500'>
            {row.original.subNumber ?? '-'}
          </span>
        ),
      },
      {
        // Apólice + Endosso agrupados (empilhados) para reduzir a largura do grid.
        id: 'policy',
        accessorKey: 'policyCode',
        header: 'Apólice',
        size: 100,
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <span className='text-xs text-alba-black-500'>
              {row.original.policyCode ?? '-'}
            </span>
            <span className='text-[11px] text-alba-gray-600'>
              End. {row.original.endorsementNumber ?? '-'}
            </span>
          </div>
        ),
      },
      {
        id: 'stipulator',
        accessorKey: 'stipulatorName',
        header: 'Estipulante',
        size: 120,
        sortable: false,
        cell: ({ row }) => (
          <span className='text-xs text-alba-black-500'>
            {row.original.stipulatorName || '-'}
          </span>
        ),
      },
      {
        id: 'issue',
        accessorKey: 'issueDate',
        header: 'Emissão',
        size: 95,
        cell: ({ row }) => <DateCell value={row.original.issueDate} />,
      },
      {
        // Parcela / Vencimento / Valor agrupados numa coluna "Parcela".
        id: 'installment',
        header: 'Parcela',
        size: 150,
        sortable: false,
        cell: ({ row }) => (
          <div className='flex flex-col gap-0.5 text-xs text-alba-black-500'>
            <span>
              <span className='text-alba-gray-600'>Parc. </span>
              {row.original.installmentNumber ?? '-'}
            </span>
            <span className='flex items-center gap-1'>
              <span className='text-alba-gray-600'>Venc. </span>
              {formatDateToBrazil(row.original.dueDate) || '-'}
            </span>
            <span>
              <span className='text-alba-gray-600'>Valor </span>
              {money(row.original.amount)}
            </span>
          </div>
        ),
      },
      {
        id: 'status',
        accessorKey: 'statusName',
        header: 'Status',
        size: 125,
        sortable: false,
        cell: ({ row }) => (
          <div className='flex flex-col gap-1'>
            <Tag.Root
              variant='outline'
              colorScheme={statusScheme(row.original.statusName)}
              size='sm'
            >
              <Tag.Label>{row.original.statusName || '-'}</Tag.Label>
            </Tag.Root>
            {row.original.paymentMethod ? (
              <span className='text-[11px] text-alba-gray-600'>
                {row.original.paymentMethod}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        // 2ª via do boleto — só para parcelas com forma de pagamento boleto.
        id: 'boleto',
        header: '2ª via',
        size: 70,
        sortable: false,
        cell: ({ row }) => <BoletoButton row={row.original} />,
      }
    );

    return cols;
  }, [showAdvisoryColumn, showBrokerColumn]);

  const sorting = useMemo(() => {
    const columnId = COLUMN_BY_SORT_FIELD[filters.sortField];
    if (!columnId) return [];
    return [{ id: columnId, desc: filters.sortType === 'desc' }];
  }, [filters.sortField, filters.sortType]);

  const handleExport = () => {
    if (!periodReady) return;
    exportMutation.mutate({
      ...activeFilters,
      sortField: queryParams.sortField,
      sortType: queryParams.sortType,
    });
  };

  return (
    <div className='flex flex-col gap-6'>
      <InstallmentsSummaryCards
        summary={summary}
        loading={summaryLoading && periodReady}
        activeStatus={filters.installmentStatus}
        onSelectStatus={status =>
          setFilters({ ...filters, page: 1, installmentStatus: status })
        }
      />

      <div className='flex flex-col gap-4'>
        <div className='flex flex-col'>
          {/* Barra: busca + botão Filtros (esquerda) | Exportar (direita) na mesma linha. */}
          <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
            <div className='flex flex-1 items-center gap-3'>
              <div className='max-w-xl flex-1'>
                <SearchFilterField
                  {...registerFilter('search')}
                  placeholder='Buscar por apólice, CNPJ, nome...'
                  className='w-full'
                />
              </div>
              <button
                type='button'
                onClick={() => setShowFilters(value => !value)}
                aria-expanded={showFilters}
                className='flex h-10 shrink-0 items-center gap-2 rounded-lg border border-alba-gray-400 px-4 text-sm text-alba-gray-600 transition-colors hover:bg-alba-gray-100'
              >
                {showFilters ? (
                  <FunnelX className='h-4 w-4' strokeWidth={1.8} />
                ) : (
                  <Funnel className='h-4 w-4' strokeWidth={1.8} />
                )}
                Filtros
              </button>
            </div>
            <Button
              variant='primary'
              size='md'
              onClick={handleExport}
              disabled={
                !periodReady || !rows.length || exportMutation.isPending
              }
            >
              <Download className='mr-2 h-4 w-4' />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>

          <ExtratoParcelasFilters
            open={showFilters}
            hasActiveFilter={hasActiveFilter}
            userType={userType}
            values={{
              periodFrom: filters.periodFrom,
              periodTo: filters.periodTo,
              periodType: filters.periodType,
              advisoryPartyCode: filters.advisoryPartyCode,
              brokerPartyCode: filters.brokerPartyCode,
              productCode: filters.productCode,
              branchCode: filters.branchCode,
              installmentStatus: filters.installmentStatus,
            }}
            advisoryOptions={advisoryOptions}
            brokerOptions={brokerOptions}
            productOptions={filterOptions?.products ?? []}
            branchOptions={filterOptions?.branches ?? []}
            installmentStatusOptions={filterOptions?.installmentStatuses ?? []}
            periodTypeOptions={filterOptions?.periodTypes ?? []}
            periodError={periodError}
            onChange={patch => setFilters({ ...filters, ...patch, page: 1 })}
            onClear={() =>
              setFilters({
                ...filters,
                page: 1,
                advisoryPartyCode: '',
                brokerPartyCode: '',
                productCode: '',
                branchCode: '',
                installmentStatus: '',
                periodType: '1',
                periodFrom: defaultPeriod.from,
                periodTo: defaultPeriod.to,
              })
            }
          />
        </div>

        {exportMutation.isError && (
          <p className='text-sm text-alba-error-red-500'>
            {(exportMutation.error as Error).message}
          </p>
        )}

        {!periodReady ? (
          <div className='rounded-3xl bg-white p-8 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
            {periodError ??
              'Selecione um período (até 3 meses) para visualizar o extrato de parcelas.'}
          </div>
        ) : error ? (
          <div className='rounded-3xl bg-white p-8 text-center text-alba-error-red-500 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
            Não foi possível carregar o extrato de parcelas. Tente novamente
            mais tarde.
          </div>
        ) : !isLoading && !rows.length ? (
          <div className='rounded-3xl bg-white p-8 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
            Nenhuma parcela encontrada para os critérios informados.
          </div>
        ) : (
          <>
            {/* Mobile: cards no lugar da tabela. */}
            <div className='space-y-4 md:hidden'>
              {isLoading
                ? Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                    (_, index) => <ReportCardSkeleton key={index} />
                  )
                : rows.map((row, index) => (
                    <ParcelaCard
                      key={`${row.installmentId}-${row.endorsementId}-${index}`}
                      row={row}
                      showAdvisory={showAdvisoryColumn}
                      showBroker={showBrokerColumn}
                    />
                  ))}
              {pagination && pagination.totalPages > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={pagination.totalPages * filters.pageSize}
                    page={filters.page}
                    pageSize={filters.pageSize}
                    totalItems={pagination.total}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        page: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, page: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            {/* Desktop: tabela. */}
            <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
              <DataTable
                ref={dataTableRef}
                className='w-full'
                loading={isLoading}
                columns={columns}
                data={rows}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                totalItems={pagination?.total ?? 0}
                totalPages={pagination?.totalPages ?? 0}
                pageSize={filters.pageSize}
                manualSorting
                sorting={sorting}
                onSortChange={nextSorting => {
                  const nextSort = nextSorting[0];
                  const sortField = nextSort
                    ? SORT_FIELD_BY_COLUMN[nextSort.id]
                    : '';
                  setFilters({
                    ...filters,
                    page: 1,
                    sortField: sortField ?? '',
                    sortType: nextSort?.desc ? 'desc' : 'asc',
                  });
                }}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, page, pageSize })
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DateCell({ value }: { value: string | null }) {
  return (
    <span className='flex items-center gap-1 text-xs text-alba-black-500'>
      <Calendar className='h-3 w-3 shrink-0 text-alba-gray-500' />
      {formatDateToBrazil(value) || '-'}
    </span>
  );
}

function TwoLine({
  top,
  bottom,
}: {
  top?: string | null;
  bottom?: string | null;
}) {
  return (
    <div className='flex flex-col'>
      <span className='text-xs font-semibold text-alba-black-500'>
        {top || '-'}
      </span>
      {bottom ? (
        <span className='text-[11px] text-alba-gray-600'>{bottom}</span>
      ) : null}
    </div>
  );
}

/** Card mobile de uma parcela (equivalente a uma linha da tabela). */
function ParcelaCard({
  row,
  showAdvisory,
  showBroker,
}: {
  row: Installment;
  showAdvisory: boolean;
  showBroker: boolean;
}) {
  return (
    <ReportCard>
      <ReportCardHeader
        title={row.clientName}
        subtitle={row.clientTaxId}
        right={
          <Tag.Root
            variant='outline'
            colorScheme={statusScheme(row.statusName)}
            size='sm'
          >
            <Tag.Label>{row.statusName || '-'}</Tag.Label>
          </Tag.Root>
        }
      />
      <ReportCardBody>
        {showAdvisory && (
          <CardRow label='Assessoria'>
            <span className='block truncate'>{row.advisoryName || '-'}</span>
          </CardRow>
        )}
        {showBroker && (
          <CardRow label='Corretor'>
            <span className='block truncate'>{row.brokerName || '-'}</span>
          </CardRow>
        )}
        <CardRow label='Produto'>
          <span className='block truncate'>{row.productName || '-'}</span>
        </CardRow>
        <CardRow label='Cliente'>
          <span className='block truncate'>{row.clientName || '-'}</span>
        </CardRow>
        <CardRow label='Sub'>{row.subNumber ?? '-'}</CardRow>
        <CardRow label='Apólice'>{row.policyCode ?? '-'}</CardRow>
        <CardRow label='Endosso'>{row.endorsementNumber ?? '-'}</CardRow>
        <CardRow label='Estipulante'>
          <span className='block truncate'>{row.stipulatorName || '-'}</span>
        </CardRow>
        <CardRow label='Emissão'>
          {formatDateToBrazil(row.issueDate) || '-'}
        </CardRow>
        <CardRow label='Parcela'>{row.installmentNumber ?? '-'}</CardRow>
        <CardRow label='Vencimento'>
          {formatDateToBrazil(row.dueDate) || '-'}
        </CardRow>
        <CardRow label='Valor'>{money(row.amount)}</CardRow>
        <CardRow label='Forma de Pagamento'>
          <span className='block truncate'>{row.paymentMethod || '-'}</span>
        </CardRow>
        <CardRow label='2ª via'>
          <div className='flex justify-end'>
            <BoletoButton row={row} />
          </div>
        </CardRow>
      </ReportCardBody>
    </ReportCard>
  );
}

/** Botão de 2ª via do boleto de uma parcela. Autocontido (estado próprio). */
function BoletoButton({ row }: { row: Installment }) {
  const boleto = useInstallmentBoleto();
  const isBoleto = (row.paymentMethod ?? '').toLowerCase().includes('boleto');
  const canDownload =
    isBoleto && Boolean(row.endorsementId) && Boolean(row.installmentId);
  // Corretora da linha inativa: travado aqui; a Api também barra (403).
  const isBlocked = isRowBoletoBlocked(row);

  if (!canDownload) {
    return <span className='text-xs text-alba-gray-400'>-</span>;
  }

  const handle = () => {
    if (isBlocked) return;
    boleto.mutate(
      {
        endorsementId: row.endorsementId as number,
        installmentId: row.installmentId as number,
        installmentNumber: row.installmentNumber,
        brokerTaxId: row.brokerTaxId,
      },
      {
        onError: err =>
          toaster.create({
            type: 'error',
            title: 'Não foi possível baixar o boleto',
            description: (err as Error).message,
          }),
      }
    );
  };

  return (
    <button
      type='button'
      onClick={handle}
      disabled={boleto.isPending || isBlocked}
      title={rowBoletoBlockedReason(row) ?? 'Baixar 2ª via do boleto'}
      className='flex h-7 w-7 items-center justify-center rounded text-alba-gray-600 transition-colors hover:bg-alba-gray-100 hover:text-alba-blue-500 disabled:opacity-50'
    >
      {boleto.isPending ? (
        <Loader2 className='h-4 w-4 animate-spin' />
      ) : (
        <FileDown className='h-4 w-4' />
      )}
    </button>
  );
}
